import { formatISODate } from "../utils/datetime"

/*
  One patient feedback response.

  Shared by the Video Logs engagement card and the QR Scan Analytics drawer so
  the same answers always read the same way — including the null case, which
  means something different per question ("N/A" vs "didn't read it").
*/

const answerLabel = (value, nullLabel) => {
  if (value === true)
    return { text: "Yes", color: "text-green-700 bg-green-50 border-green-200" }
  if (value === false)
    return { text: "No", color: "text-red-700 bg-red-50 border-red-200" }
  return { text: nullLabel, color: "text-gray-500 bg-gray-50 border-gray-200" }
}

const Answer = ({ question, value, nullLabel }) => {
  const { text, color } = answerLabel(value, nullLabel)

  return (
    <div className="flex items-center justify-between gap-3 text-xs py-1.5">
      <span className="text-gray-600">{question}</span>
      <span
        className={`px-2 py-0.5 rounded-full border font-medium shrink-0 ${color}`}
      >
        {text}
      </span>
    </div>
  )
}

const FeedbackResponseCard = ({ response, index }) => (
  <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
    <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
        {typeof index === "number" ? `Response ${index + 1} · ` : ""}
        {formatISODate(response.CreatedAt)}
      </span>
    </div>

    <div className="px-3 py-2 divide-y divide-gray-100">
      <Answer
        nullLabel="N/A"
        question="Illustrations helped understanding"
        value={response.IllustrationsHelpful}
      />
      <Answer
        nullLabel="N/A"
        question="Would recommend this report"
        value={response.RecommendReport}
      />
      <Answer
        nullLabel="Didn't read it"
        question="Simplified explanation helpful"
        value={response.SpeHelpful}
      />
      {/* Sits with the other answers rather than as a floating badge — on its
          own, a value like "Patient portal" does not say what it answers. Plain
          text, not a pill: the pills carry yes/no meaning through colour, which
          a delivery method has none of. */}
      <div className="flex items-center justify-between gap-3 text-xs py-1.5">
        <span className="text-gray-600">How they received their report</span>
        <span
          className={`shrink-0 ${
            response.ReportDelivery
              ? "font-medium text-gray-800"
              : "text-gray-400"
          }`}
        >
          {response.ReportDelivery || "Not answered"}
        </span>
      </div>
    </div>

    {response.Comments && (
      <div className="px-3 pb-3 pt-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">
          Comment
        </span>
        <p className="mt-1 text-xs text-gray-700 italic leading-relaxed whitespace-pre-wrap bg-gray-50 border-l-2 border-gray-300 rounded-r px-3 py-2">
          {response.Comments}
        </p>
      </div>
    )}
  </div>
)

export default FeedbackResponseCard
